import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import {
  UserX, Clock, AlertTriangle, Eye, Calendar, Plus, Search,
  Loader2, RefreshCw, ChevronDown, ChevronUp, Mail, MessageSquare
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const DEACTIVATION_REASONS_MAP = {
  'too_expensive': 'Too Expensive',
  'not_enough_features': 'Missing Features',
  'switching_competitor': 'Switching to Competitor',
  'business_closing': 'Business Closing',
  'technical_issues': 'Technical Issues',
  'poor_support': 'Poor Support',
  'not_using': 'Not Using Platform',
  'temporary_pause': 'Temporary Pause',
  'other': 'Other'
};

const DeactivatedVendors = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('days_remaining');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [extensionDays, setExtensionDays] = useState(30);
  const [extending, setExtending] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    total_deactivated: 0,
    expiring_soon: 0,
    extended_count: 0
  });

  useEffect(() => {
    fetchDeactivatedVendors();
  }, []);

  const fetchDeactivatedVendors = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/api/vendor-subscription/deactivated-vendors');
      setVendors(response.vendors || []);
      setStats(response.stats || {
        total_deactivated: response.vendors?.length || 0,
        expiring_soon: response.vendors?.filter(v => v.days_remaining <= 7).length || 0,
        extended_count: response.vendors?.filter(v => v.retention_extended).length || 0
      });
    } catch (error) {
      console.error('Failed to fetch deactivated vendors:', error);
      // Mock data for demonstration
      const mockVendors = [
        {
          id: 'vendor-001',
          vendor_name: 'ABC Electronics',
          email: 'abc@example.com',
          phone: '+91 98765 43210',
          deactivation_date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
          deletion_scheduled_date: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString(),
          days_remaining: 45,
          primary_reason: 'too_expensive',
          all_reasons: ['too_expensive', 'not_using'],
          other_reason: '',
          plan_name: 'Professional',
          listings_count: 25,
          retention_extended: false,
          extension_history: []
        },
        {
          id: 'vendor-002',
          vendor_name: 'Fashion Hub',
          email: 'fashion@example.com',
          phone: '+91 98765 43211',
          deactivation_date: new Date(Date.now() - 53 * 24 * 60 * 60 * 1000).toISOString(),
          deletion_scheduled_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          days_remaining: 7,
          primary_reason: 'switching_competitor',
          all_reasons: ['switching_competitor'],
          other_reason: '',
          plan_name: 'Business',
          listings_count: 150,
          retention_extended: false,
          extension_history: []
        },
        {
          id: 'vendor-003',
          vendor_name: 'Home Decor Plus',
          email: 'homedecor@example.com',
          phone: '+91 98765 43212',
          deactivation_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          deletion_scheduled_date: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
          days_remaining: 60,
          primary_reason: 'temporary_pause',
          all_reasons: ['temporary_pause'],
          other_reason: 'Taking a break for the season',
          plan_name: 'Basic',
          listings_count: 10,
          retention_extended: true,
          extension_history: [{ days: 30, date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString() }]
        }
      ];
      setVendors(mockVendors);
      setStats({
        total_deactivated: mockVendors.length,
        expiring_soon: mockVendors.filter(v => v.days_remaining <= 7).length,
        extended_count: mockVendors.filter(v => v.retention_extended).length
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExtendRetention = async () => {
    if (!selectedVendor || extensionDays <= 0) return;
    
    setExtending(true);
    try {
      await apiCall(`/api/vendor-subscription/extend-retention/${selectedVendor.id}`, {
        method: 'POST',
        body: JSON.stringify({ extension_days: extensionDays })
      });
      
      toast({
        title: 'Retention Extended',
        description: `Retention period for ${selectedVendor.vendor_name} extended by ${extensionDays} days.`
      });
      
      setShowExtendModal(false);
      setSelectedVendor(null);
      setExtensionDays(30);
      fetchDeactivatedVendors();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to extend retention period',
        variant: 'destructive'
      });
    } finally {
      setExtending(false);
    }
  };

  const openDetailsModal = (vendor) => {
    setSelectedVendor(vendor);
    setShowDetailsModal(true);
  };

  const openExtendModal = (vendor) => {
    setSelectedVendor(vendor);
    setExtensionDays(30);
    setShowExtendModal(true);
  };

  const getDaysRemainingColor = (days) => {
    if (days <= 7) return 'text-red-600 bg-red-50';
    if (days <= 14) return 'text-amber-600 bg-amber-50';
    if (days <= 30) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const getDaysRemainingBadge = (days) => {
    if (days <= 7) return 'bg-red-100 text-red-700 border-red-200';
    if (days <= 14) return 'bg-amber-100 text-amber-700 border-amber-200';
    if (days <= 30) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-green-100 text-green-700 border-green-200';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Filter and sort vendors
  const filteredVendors = vendors
    .filter(v => 
      v.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.id?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const order = sortOrder === 'asc' ? 1 : -1;
      return aVal > bVal ? order : -order;
    });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <UserX className="w-7 h-7 text-slate-600" />
              Deactivated Vendors
            </h1>
            <p className="text-slate-500 mt-1">
              Manage vendors in the 60-day retention period before permanent deletion
            </p>
          </div>
          <Button onClick={fetchDeactivatedVendors} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Deactivated</p>
                  <p className="text-2xl font-bold text-slate-800">{stats.total_deactivated}</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                  <UserX className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Expiring Soon (≤7 days)</p>
                  <p className="text-2xl font-bold text-red-700">{stats.expiring_soon}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Retention Extended</p>
                  <p className="text-2xl font-bold text-blue-700">{stats.extended_count}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Plus className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by vendor name, email, or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Badge variant="outline" className="text-slate-600">
                {filteredVendors.length} vendors
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Vendors Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Deactivated Accounts</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left p-4 font-semibold text-slate-600">Vendor</th>
                    <th 
                      className="text-left p-4 font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleSort('deactivation_date')}
                    >
                      <div className="flex items-center gap-1">
                        Deactivation Date
                        {sortField === 'deactivation_date' && (
                          sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="text-center p-4 font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleSort('days_remaining')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Days Remaining
                        {sortField === 'days_remaining' && (
                          sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th className="text-left p-4 font-semibold text-slate-600">Reason</th>
                    <th className="text-left p-4 font-semibold text-slate-600">Plan</th>
                    <th className="text-center p-4 font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVendors.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center p-8 text-slate-500">
                        No deactivated vendors found
                      </td>
                    </tr>
                  ) : (
                    filteredVendors.map((vendor) => (
                      <tr key={vendor.id} className="border-b hover:bg-slate-50">
                        <td className="p-4">
                          <div>
                            <p className="font-medium text-slate-800">{vendor.vendor_name}</p>
                            <p className="text-sm text-slate-500">{vendor.email}</p>
                            <p className="text-xs text-slate-400">ID: {vendor.id}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-700">{formatDate(vendor.deactivation_date)}</span>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <Badge className={`${getDaysRemainingBadge(vendor.days_remaining)} font-bold`}>
                            <Clock className="w-3 h-3 mr-1" />
                            {vendor.days_remaining} Days
                          </Badge>
                          {vendor.retention_extended && (
                            <Badge variant="outline" className="ml-2 text-xs bg-blue-50 text-blue-600">
                              Extended
                            </Badge>
                          )}
                        </td>
                        <td className="p-4">
                          <Badge variant="outline" className="bg-slate-100">
                            {DEACTIVATION_REASONS_MAP[vendor.primary_reason] || vendor.primary_reason}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <span className="text-slate-700">{vendor.plan_name}</span>
                          <p className="text-xs text-slate-400">{vendor.listings_count} listings</p>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-center gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => openDetailsModal(vendor)}
                              className="text-slate-600"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Details
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openExtendModal(vendor)}
                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Extend
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* View Details Modal */}
        <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserX className="w-5 h-5 text-slate-600" />
                Vendor Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedVendor && (
              <div className="space-y-4 py-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-bold text-lg text-slate-800">{selectedVendor.vendor_name}</h3>
                  <p className="text-slate-600">{selectedVendor.email}</p>
                  <p className="text-sm text-slate-500">{selectedVendor.phone}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Deactivated</p>
                    <p className="font-semibold">{formatDate(selectedVendor.deactivation_date)}</p>
                  </div>
                  <div className={`rounded-lg p-3 ${getDaysRemainingColor(selectedVendor.days_remaining)}`}>
                    <p className="text-xs opacity-75">Days Remaining</p>
                    <p className="font-bold text-xl">{selectedVendor.days_remaining}</p>
                  </div>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-700 mb-2">Deactivation Reasons</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedVendor.all_reasons?.map((reason, idx) => (
                      <Badge key={reason.id || reason.name || `${reason.title || "reason"}-${idx}`} variant="outline" className="bg-white">
                        {DEACTIVATION_REASONS_MAP[reason] || reason}
                      </Badge>
                    ))}
                  </div>
                  {selectedVendor.other_reason && (
                    <div className="mt-3 p-3 bg-white rounded border">
                      <p className="text-xs text-slate-500 mb-1">Additional Comments:</p>
                      <p className="text-sm text-slate-700">{selectedVendor.other_reason}</p>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Previous Plan</p>
                    <p className="font-semibold">{selectedVendor.plan_name}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Listings</p>
                    <p className="font-semibold">{selectedVendor.listings_count}</p>
                  </div>
                </div>
                
                {selectedVendor.extension_history?.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-700 mb-2">Extension History</h4>
                    {selectedVendor.extension_history.map((ext, idx) => (
                      <div key={ext.id || ext.name || `${ext.title || "ext"}-${idx}`} className="flex justify-between text-sm">
                        <span>+{ext.days} days</span>
                        <span className="text-blue-600">{formatDate(ext.date)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                Close
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  setShowDetailsModal(false);
                  openExtendModal(selectedVendor);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Extend Retention
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Extend Retention Modal */}
        <Dialog open={showExtendModal} onOpenChange={setShowExtendModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-blue-600" />
                Extend Data Retention
              </DialogTitle>
              <DialogDescription>
                Extend the data retention period for {selectedVendor?.vendor_name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-blue-700">Current Days Remaining:</span>
                  <Badge className={getDaysRemainingBadge(selectedVendor?.days_remaining || 0)}>
                    {selectedVendor?.days_remaining} days
                  </Badge>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="extension_days">Extension Period (days)</Label>
                <Input
                  id="extension_days"
                  type="number"
                  min={1}
                  max={365}
                  value={extensionDays}
                  onChange={(e) => setExtensionDays(parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-slate-500">
                  New total: {(selectedVendor?.days_remaining || 0) + extensionDays} days
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Mail className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-900">Vendor Notification</h4>
                    <p className="text-sm text-amber-700">
                      The vendor will receive an email notification about this extension.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExtendModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleExtendRetention}
                disabled={extending || extensionDays <= 0}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {extending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Extending...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Extend by {extensionDays} Days
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default DeactivatedVendors;
